// Las secciones del portal del proveedor, en el orden de la barra superior.
// Es el unico lugar que las enumera: una ficha que llena una seccion (Inicio,
// PP-64; Vencimientos, PP-23) cambia su `render` aca y no toca el shell.
//
// Viajes lee hoy una planta por vez: con el filtro en "Todas" pide elegir una.
// Nomina con "Todas" es la nomina unica de la empresa (PP-70, una lectura de
// todas las plantas); con una planta, la de esa planta. Inicio (PP-64) lee
// todas las plantas juntas con las lecturas multiplanta de la API (PP-63).
// Vencimientos (PP-23) junta todas las plantas desde el portal. Mapa (PP-51)
// lee las posiciones de toda la flota con una lectura multiplanta y respeta
// el filtro de planta.
// Exportable (LQ-1, ADR-026) lee una planta por vez, como Viajes: con "Todas"
// pide elegir una. Es el detalle de viajes sin montos y sirve a todo par.
// Liquidacion (LQ-4) es la liquidacion con montos de los pares en modalidad
// full: con "Todas", el resumen multiplanta (ADR-025); con una planta, el
// borrador del periodo con sus lineas y la descarga con montos. Una planta
// en export_only lo dice y manda al Exportable.
import type { FleetPort } from "../fleet/port.js";
import { Expiries } from "../expiries/index.js";
import { AllSitesFleet } from "../fleet/AllSitesFleet.js";
import { Fleet } from "../fleet/Fleet.js";
import { FleetMap } from "../fleet-map/FleetMap.js";
import { Home } from "../home/index.js";
import { accessesForSite } from "../logic/navigation.js";
import type { MultiSitePort } from "../multi-site/port.js";
import type { Clock } from "../requests/clock.js";
import type { RequestsPort } from "../requests/port.js";
import { TripsSection } from "../requests/TripsSection.js";
import { Settlement, TripExport, type SaveFile, type SettlementPort, type TripExportPort } from "../settlements/index.js";
import type { PortalSection } from "../shell/types.js";
import { SiteScoped } from "./SiteScoped.js";

export interface SectionDeps {
  fleetPort: FleetPort;
  requestsPort: RequestsPort;
  /** Lecturas de todas las plantas juntas (PP-63), para el Inicio y el Mapa. */
  multiSitePort: MultiSitePort;
  /** Reloj de la bandeja y del Inicio; los tests lo reemplazan. */
  clock?: Clock;
  /** Exportable de viajes sin montos (LQ-1). */
  tripExportPort: TripExportPort;
  /** Liquidacion con montos de los pares en full (LQ-4). */
  settlementPort: SettlementPort;
  /** Entrega del archivo descargado; los tests la reemplazan. */
  saveFile?: SaveFile;
}

export const SECTION_PATHS = {
  home: "/",
  trips: "/trips",
  map: "/map",
  roster: "/roster",
  expiries: "/expiries",
  settlements: "/settlements",
  tripExport: "/trip-export",
} as const;

export function portalSections({ fleetPort, requestsPort, multiSitePort, clock, tripExportPort, settlementPort, saveFile }: SectionDeps): readonly PortalSection[] {
  return [
    {
      path: SECTION_PATHS.home,
      label: "Inicio",
      icon: "inicio",
      // Con "Todas", todas las plantas de la cuenta; con una, solo esa (PP-64).
      // Las ofertas las trae la encuesta del portal (Portal.tsx, PP-71).
      render: (context) => (
        <Home
          context={context}
          port={multiSitePort}
          requestsPort={requestsPort}
          fleetPort={fleetPort}
          {...(clock === undefined ? {} : { clock })}
        />
      ),
    },
    {
      path: SECTION_PATHS.trips,
      label: "Viajes",
      icon: "viajes",
      // Bandeja por planta y vista "Carga por franja" (rediseño): TripsSection.
      render: (context) => (
        <TripsSection
          context={context}
          requestsPort={requestsPort}
          fleetPort={fleetPort}
          multiSitePort={multiSitePort}
          {...(clock === undefined ? {} : { clock })}
        />
      ),
    },
    {
      path: SECTION_PATHS.map,
      label: "Mapa",
      icon: "mapa",
      wide: true,
      // Con "Todas", la flota de todas las plantas; con una, la de esa (PP-51).
      render: (context) => <FleetMap context={context} port={multiSitePort} {...(clock === undefined ? {} : { clock })} />,
    },
    {
      path: SECTION_PATHS.roster,
      label: "Nómina",
      icon: "nomina",
      render: (context) =>
        context.siteId === null ? (
          <AllSitesFleet session={context.session} port={fleetPort} sites={context.sites} />
        ) : (
          <SiteScoped context={context} purpose="ver sus vehículos y conductores">
            {(site) => <Fleet session={context.session} port={fleetPort} site={site} />}
          </SiteScoped>
        ),
    },
    {
      path: SECTION_PATHS.expiries,
      label: "Vencimientos",
      icon: "vencimientos",
      // Con "Todas", todas las plantas de la cuenta; con una planta filtrada,
      // solo sus habilitaciones. Renovar se hace en la fila (PP-23).
      render: (context) => (
        <Expiries
          session={context.session}
          port={fleetPort}
          sites={context.siteId === null ? context.sites : accessesForSite(context.sites, context.siteId)}
        />
      ),
    },
    {
      path: SECTION_PATHS.settlements,
      label: "Liquidación",
      icon: "liquidacion",
      // Con "Todas", el resumen de todas las plantas (ADR-025); con una, su borrador.
      render: (context) => <Settlement context={context} port={settlementPort} {...(saveFile === undefined ? {} : { save: saveFile })} />,
    },
    {
      path: SECTION_PATHS.tripExport,
      label: "Exportable",
      icon: "exportable",
      // Una vista por planta, no por habilitacion: la API arma el exportable
      // de la empresa madre en la planta, con todas sus habilitaciones.
      render: (context) => {
        const accesses = accessesForSite(context.sites, context.siteId);
        const first = accesses[0];
        if (first === undefined) {
          return (
            <SiteScoped context={context} purpose="bajar su exportable de viajes">
              {() => null}
            </SiteScoped>
          );
        }
        return (
          <TripExport
            key={first.siteId}
            session={context.session}
            port={tripExportPort}
            site={{ siteId: first.siteId, name: first.name, providerIds: accesses.map((a) => a.providerId) }}
            {...(saveFile === undefined ? {} : { save: saveFile })}
          />
        );
      },
    },
  ];
}
